import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getBuildingTypesForForms } from 'features/bpList/ducks/selectors'
import Select from 'components/atoms/Select'
import { not, propOr, values, equals, isEmpty } from 'ramda'
import styled from 'styled-components'
import Input from 'components/atoms/Input'
import { validateBuildingDetailsField } from 'features/createBp/ducks/schema'
import { isNotEmpty, isNotNilOrEmpty } from 'utils/ramda'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import { getBuildingTypeValueType } from 'utils/bpData'

const BuildingTypeSelect = ({
  disabled,
  lockTypeSelection = false,
  onChange,
  index,
  value,
  withDelete,
  onDelete,
  blockDwelling,
  allowZeroUnits = false,
  suggestedUnitsCount = null,
  onUnlockTypeSelection = () => {}
}) => {
  const [selectedType, setSelectedType] = useState({
    type: propOr('', 'type', value),
    value: propOr('', 'value', value),
    unit: propOr('', 'unit', value),
    description: propOr('', 'description', value),
    units: propOr(1, 'units', value),
    index
  })
  const buildingTypes = useSelector(getBuildingTypesForForms)
  const lastPropagatedValue = useRef(null)
  const isDwellingSoleUnit =
    selectedType.type === 'residential.dwelling.sole_unit'
  const isHotel = selectedType.type === 'hotel'

  useEffect(() => {
    const def = {
      type: propOr('', 'type', value),
      value: propOr('', 'value', value),
      unit: propOr('', 'unit', value),
      description: propOr('', 'description', value),
      units: propOr(1, 'units', value),
      index
    }

    if (not(equals(def, selectedType))) {
      setSelectedType(def)
    }
  }, [value])

  const selectOptions = useMemo(() => {
    if (buildingTypes) {
      const options = values(buildingTypes)
        .map(type => ({
          label: getBuildingTypeLabel(type),
          value: type.key,
          unit: type.unit,
          multipleBpUnits: type.multipleBpUnits
        }))
        .filter(e => e.value !== 'public')
      return blockDwelling
        ? options.filter(
            option => option.value !== 'residential.dwelling.sole_unit'
          )
        : options
    }
  }, [buildingTypes, selectedType, blockDwelling])

  const unit = useMemo(() => {
    if (isNotNilOrEmpty(selectOptions)) {
      const item = selectOptions.find(e => e.value === selectedType.type)
      return propOr('', 'unit', item)
    }
  }, [selectedType])

  const handleSelectChange = (_, type) => {
    const foundOption = selectOptions.find(option => option.value === type)
    const shouldSeedUnits =
      Number.isInteger(suggestedUnitsCount) &&
      suggestedUnitsCount > 0 &&
      foundOption?.multipleBpUnits &&
      foundOption?.unit === 'sqm'
    const shouldSeedUnitsValue =
      Number.isInteger(suggestedUnitsCount) &&
      suggestedUnitsCount > 0 &&
      foundOption?.multipleBpUnits &&
      foundOption?.unit === 'unit'

    setSelectedType(prev => ({
      ...prev,
      type,
      unit: foundOption.unit,
      units: shouldSeedUnits
        ? suggestedUnitsCount
        : Number(prev.units) > 0
          ? Number(prev.units)
          : 1,
      value:
        type === 'residential.dwelling.sole_unit'
          ? 1
          : shouldSeedUnitsValue &&
              (!isNotNilOrEmpty(prev.value) || String(prev.value) === '0')
            ? String(suggestedUnitsCount)
            : allowZeroUnits && !isNotNilOrEmpty(prev.value)
              ? '0'
              : prev.value
    }))
  }

  const handleInputChange = (name, value) => {
    setSelectedType(prev => ({
      ...prev,
      [name]: value
    }))
  }

  useEffect(() => {
    const nextValue = (() => {
      const rawValue = String(selectedType.value ?? '')
      if (rawValue === '' || rawValue === '0') {
        return allowZeroUnits ? '0' : ''
      }

      return rawValue.replace(/^0+/, '')
    })()

    const nextPayload = {
      ...selectedType,
      description: selectedType.description,
      value: nextValue,
      index
    }

    if (not(equals(lastPropagatedValue.current, nextPayload))) {
      lastPropagatedValue.current = nextPayload
      onChange(nextPayload)
    }
  }, [selectedType, allowZeroUnits, unit, index])

  return (
    <Wrapper>
      {lockTypeSelection && index === 0 && (
        <AutofillNotice>
          <AutofillText>
            Auto-filled from the address reference data. You can change it if it does not
            look right.
          </AutofillText>
          <ChangeTypeButton type='button' onClick={onUnlockTypeSelection}>
            Change
          </ChangeTypeButton>
        </AutofillNotice>
      )}
      <Select
        name='type'
        options={selectOptions}
        placeholder='Select'
        value={selectedType.type}
        label='Building type'
        onChange={handleSelectChange}
        validate={validateBuildingDetailsField(selectedType)}
        withDelete={withDelete && !lockTypeSelection && !disabled}
        onDelete={onDelete}
        disabled={disabled || lockTypeSelection}
      />
      {isNotEmpty(selectedType.type) && (
        <ValueWrapper>
          {!isDwellingSoleUnit && (
            <Input
              label={getBuildingTypeValueType(selectedType.type)}
              value={
                isEmpty(selectedType.value)
                  ? allowZeroUnits
                    ? '0'
                    : 0
                  : selectedType.value
              }
              name='value'
              onChange={handleInputChange}
              validate={validateBuildingDetailsField(selectedType)}
              disabled={disabled || isDwellingSoleUnit}
            />
          )}
          {unit === 'unit' && (
            <Input
              label='Description'
              value={selectedType.description}
              name='description'
              optional
              multiline
              rows={4}
              onChange={handleInputChange}
              validate={validateBuildingDetailsField(selectedType)}
              disabled={disabled || lockTypeSelection}
              placeholder={
                isHotel
                  ? 'Building offering paid lodging on a short basis (i.e. it is not the main residence of the inhabitants)'
                  : 'e.g. Detached two storey house. Brick construction with wooden cladding.'
              }
            />
          )}
          {unit !== 'unit' && isNotNilOrEmpty(selectedType.description) && (
            <Input
              label='Description'
              value={selectedType.description}
              name='description'
              optional
              multiline
              rows={3}
              onChange={handleInputChange}
              validate={validateBuildingDetailsField(selectedType)}
              disabled
              placeholder=''
            />
          )}
          {isDwellingSoleUnit && (
            <DescriptionWrapper>
              <InputDescription>
                This type of building is one-time subscription. You cannot add
                more building types to this one.
              </InputDescription>
            </DescriptionWrapper>
          )}
        </ValueWrapper>
      )}
    </Wrapper>
  )
}

export default BuildingTypeSelect

const getBuildingTypeLabel = type => {
  if (type.key === 'residential.dwelling.sole_unit') {
    return 'Residential (single dwelling)'
  }

  return type.label
}

const Wrapper = styled.div`
  &:not(:first-of-type) {
    padding-top: 22px;
    border-top: 1px solid ${({ theme }) => theme.colors.grey[500]};
    margin-top: 30px;
  }
`

const ValueWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 15px 15px 0;
`

const DescriptionWrapper = styled.div`
  margin-bottom: 20px;
`

const AutofillNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 4px;
  margin-bottom: 14px;
  background-color: rgba(51, 190, 204, 0.07);
  padding: 9px 10px;
`

const AutofillText = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.haiti};
`

const ChangeTypeButton = styled.button`
  flex-shrink: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 2px 0;
  text-transform: uppercase;

  &:hover {
    text-decoration: underline;
  }
`
